import React, { useState, useEffect, useRef } from 'react';
import { ClockIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import api from '../services/api';

const ScadenzeOfferteDropdown = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scadenze, setScadenze] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch scadenze offerte
  const fetchScadenze = async () => {
    if (!user || user.role !== 'admin') return;
    
    try {
      setLoading(true);
      const response = await api.get('/notifiche/scadenze-offerte');
      
      if (response.data.success) {
        setScadenze(response.data.scadenze || []);
      }
    } catch (error) {
      console.error('Errore caricamento scadenze offerte:', error);
    } finally {
      setLoading(false);
    }
  };

  // Carica scadenze al mount e ogni 60 secondi
  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchScadenze();
      const interval = setInterval(fetchScadenze, 60000); // ogni minuto
      return () => clearInterval(interval);
    }
  }, [user]);

  // Gestione click fuori dal dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Marca scadenza come letta
  const marcaComeLetta = async (notificaId) => {
    try {
      await api.put(`/notifiche/${notificaId}/letta`);
      // Aggiorna lo stato locale
      setScadenze(prev => prev.filter(s => s.id !== notificaId));
    } catch (error) {
      console.error('Errore marcatura scadenza:', error);
    }
  };

  // Marca tutte come lette
  const marcaTutteComeLette = async () => {
    try {
      // Marca solo le scadenze offerte come lette
      await Promise.all(
        scadenze.map(s => api.put(`/notifiche/${s.id}/letta`))
      );
      setScadenze([]);
    } catch (error) {
      console.error('Errore marcatura tutte scadenze:', error);
    }
  };

  // Non mostrare se non è admin
  if (!user || user.role !== 'admin') {
    return null;
  }

  // Conta scadenze non lette
  const scadenzeNonLette = scadenze.filter(s => !s.letta).length;

  // Funzione per ottenere il colore in base all'urgenza
  const getUrgenzaColor = (urgenza) => {
    switch (urgenza) {
      case 'alta': return 'text-red-600 bg-red-50 border-red-200';
      case 'media': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'bassa': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Icona Clock con badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
        title="Scadenze Offerte"
      >
        <ClockIcon className="h-6 w-6" />
        {scadenzeNonLette > 0 && (
          <>
            <span className="absolute top-1 right-1 flex h-5 w-5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-5 w-5 bg-orange-500 text-white text-xs items-center justify-center font-semibold">
                {scadenzeNonLette > 9 ? '9+' : scadenzeNonLette}
              </span>
            </span>
          </>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[600px] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-yellow-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <ClockIcon className="h-5 w-5 text-orange-600" />
                <h3 className="text-sm font-semibold text-gray-900">
                  Scadenze Offerte
                </h3>
                {scadenzeNonLette > 0 && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-orange-500 text-white rounded-full">
                    {scadenzeNonLette}
                  </span>
                )}
              </div>
              {scadenze.length > 0 && (
                <button
                  onClick={marcaTutteComeLette}
                  className="text-xs text-orange-600 hover:text-orange-800 font-medium"
                >
                  Marca tutte lette
                </button>
              )}
            </div>
          </div>

          {/* Lista scadenze */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-500">Caricamento...</p>
              </div>
            ) : scadenze.length === 0 ? (
              <div className="p-8 text-center">
                <ClockIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Nessuna scadenza offerta</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {scadenze.map((scadenza) => (
                  <div
                    key={scadenza.id}
                    className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                      !scadenza.letta ? 'bg-orange-50' : ''
                    }`}
                    onClick={() => {
                      if (scadenza.link) {
                        window.location.href = scadenza.link;
                      }
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        {/* Badge urgenza */}
                        <div className="flex items-center space-x-2 mb-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getUrgenzaColor(scadenza.urgenza)}`}>
                            {scadenza.urgenza === 'alta' && '🔴 Urgente'}
                            {scadenza.urgenza === 'media' && '🟠 Media'}
                            {scadenza.urgenza === 'bassa' && '🔵 Bassa'}
                          </span>
                          {!scadenza.letta && (
                            <span className="h-2 w-2 bg-orange-500 rounded-full"></span>
                          )}
                        </div>

                        {/* Titolo */}
                        <h4 className="text-sm font-semibold text-gray-900 mb-1">
                          {scadenza.titolo}
                        </h4>

                        {/* Messaggio */}
                        <p className="text-sm text-gray-600 mb-2">
                          {scadenza.messaggio}
                        </p>

                        {/* Info aggiuntive */}
                        {scadenza.datiAggiuntivi && (
                          <div className="text-xs text-gray-500 space-y-1">
                            {scadenza.datiAggiuntivi.vettura && (
                              <div>📋 {scadenza.datiAggiuntivi.vettura}</div>
                            )}
                            {scadenza.datiAggiuntivi.giorniRimanenti !== undefined && (
                              <div>
                                ⏰ {scadenza.datiAggiuntivi.giorniRimanenti === 0 
                                  ? 'Scade oggi'
                                  : scadenza.datiAggiuntivi.giorniRimanenti === 1
                                  ? 'Scade domani'
                                  : `Scade in ${scadenza.datiAggiuntivi.giorniRimanenti} giorni`
                                }
                              </div>
                            )}
                          </div>
                        )}

                        {/* Timestamp */}
                        <p className="text-xs text-gray-400 mt-2">
                          {formatDistanceToNow(new Date(scadenza.dataNotifica), { 
                            addSuffix: true, 
                            locale: it 
                          })}
                        </p>
                      </div>

                      {/* Pulsante chiudi */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          marcaComeLetta(scadenza.id);
                        }}
                        className="ml-2 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        title="Marca come letta"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {scadenze.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  window.location.href = '/nlt/gestione-offerte-vetture';
                  setIsOpen(false);
                }}
                className="w-full text-center text-sm text-orange-600 hover:text-orange-800 font-medium"
              >
                Vai a Gestione Offerte →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScadenzeOfferteDropdown;

